import React, { useState } from 'react';
import axios from 'axios';
import { 
  PlusCircle, Image as ImageIcon, Star,
  X, Loader2, Check, Ruler, Package
} from 'lucide-react';
import { useAuth } from "../../context/AuthContext";

const AddProduct = () => {
  const { user } = useAuth();
  // Safe extraction of token
  const adminToken = user?.accessToken || user?.token;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const CLOUD_NAME = "dlju8yruj"; 
  const UPLOAD_PRESET = "WE_THRIFT_PRESET";

  const [newProduct, setNewProduct] = useState({
    name: "", brand: "", description: "",
    images: "", videos: "", category: "shirts",
    actualPrice: "", salePrice: "",
    rating: 5, sizes: [], 
    sizeChart: [] 
  });

  const categoriesList = ["hoodies", "jeans", "sneakers", "t-shirts", "shirts", "jackets", "formal", "sweatshirts", "baggy pants", "track pants", "shoes"];
  const clothingSizes = ["S", "M", "L", "XL", "XXL"];
  const shoeSizes = ["UK-6", "UK-7", "UK-8", "UK-9", "UK-10", "UK-11"];

  const getChartHeaders = () => {
    const cat = newProduct.category.toLowerCase();
    const baseHeaders = (cat.includes("shoes") || cat.includes("sneakers")) 
      ? ["Size", "CM", "Euro", "UK"] 
      : (cat.includes("pant") || cat.includes("jeans")) 
      ? ["Size", "Waist", "Length", "Thigh"] 
      : ["Size", "Chest", "Length", "Sleeve"];
    return [...baseHeaders, "STOCK 📦"];
  };

  const handleSizeChartChange = (index, field, value) => {
    const updatedChart = [...newProduct.sizeChart];
    updatedChart[index][field] = value;
    setNewProduct({ ...newProduct, sizeChart: updatedChart });
  };

  const syncSizeChartWithSizes = (selectedSizes) => {
    const newChart = selectedSizes.map(size => {
      const existing = newProduct.sizeChart.find(row => row.label === size);
      return existing || { label: size, val1: "", val2: "", val3: "", stock: 1 };
    });
    setNewProduct(prev => ({ ...prev, sizes: selectedSizes, sizeChart: newChart }));
  };

  const toggleSize = (size) => {
    const updatedSizes = newProduct.sizes.includes(size) 
      ? newProduct.sizes.filter(s => s !== size) 
      : [...newProduct.sizes, size];
    syncSizeChartWithSizes(updatedSizes);
  };

  const getArray = (str) => (!str ? [] : str.split(",").map(u => u.trim()).filter(u => u !== ""));

  const uploadMedia = async (file, type = 'image') => {
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    try {
      const res = await axios.post(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${type}/upload`, formData);
      return res.data.secure_url;
    } catch (err) {
      console.error("Upload Error", err);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // 🔥 FULLY SYNCED SUBMIT LOGIC
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newProduct.sizes.length === 0) return alert("Bhai, kam se kam ek size toh select kar!");
    if (!adminToken) return alert("Bhai session expire ho gaya hai, wapas login kar!");
    
    setLoading(true);
    
    // Total stock calculation for Mongoose model
    const sizeStockData = newProduct.sizeChart.map(row => ({
      size: row.label,
      quantity: Number(row.stock) || 0
    }));
    
    const totalCalculatedStock = sizeStockData.reduce((acc, curr) => acc + curr.quantity, 0);

    // Final Data Mapping (Syncing with backend Product model)
    const productData = {
      name: newProduct.name,           // Mongoose expects 'name'
      description: newProduct.description, // Mongoose expects 'description'
      brand: newProduct.brand || "ARCHIVE",
      category: newProduct.category.toLowerCase(),
      img: getArray(newProduct.images)[0] || "", // First image as main 'img'
      gallery: getArray(newProduct.images),
      video: getArray(newProduct.videos),
      price: Number(newProduct.salePrice),
      mrp: Number(newProduct.actualPrice),
      sizes: newProduct.sizes,
      sizeStock: sizeStockData, 
      stock: totalCalculatedStock,     // Total items in stock
      inStock: totalCalculatedStock > 0,
      sizeChart: newProduct.sizeChart, 
      rating: Number(newProduct.rating),
    };

    try {
      // API Call
      await axios.post("https://wethrift-backend.onrender.com/api/product", productData, {
        headers: { token: `Bearer ${adminToken.replace(/"/g, '')}` } 
      });
      
      alert("✅ PROTOCOL SUCCESS: Product & Stock Added!");
      window.location.reload(); 
    } catch (err) {
      console.error("❌ Backend Error:", err.response?.data);
      alert("❌ UPLOAD FAILED: " + (err.response?.data?.message || err.response?.data?.error || "Server Error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-10 bg-black min-h-screen pb-20 text-white">
      {/* HEADER */}
     {/* --- FORGE ASSET HEADER (RESPONSIVE) --- */}
<div className="bg-zinc-950 border border-white/5 p-5 lg:p-8 rounded-[1.5rem] lg:rounded-[2.5rem] flex flex-col sm:flex-row items-center sm:items-center justify-between gap-6">
  
  <div className="flex flex-col sm:flex-row items-center gap-4 lg:gap-6 text-center sm:text-left">
    {/* Icon Container */}
    <div className="p-3 lg:p-4 bg-emerald-500 rounded-xl lg:rounded-2xl text-black shadow-[0_0_20px_rgba(16,185,129,0.3)]">
      <Package size={24} className="lg:w-7 lg:h-7" />
    </div>

    {/* Text Section */}
    <div className="min-w-0">
      <h2 className="text-2xl lg:text-3xl font-black italic uppercase tracking-tighter leading-none">
        Forge_New<span className="text-emerald-500 lg:text-white lg:opacity-100">_Asset</span>
      </h2>
      <p className="text-[8px] lg:text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em] lg:tracking-[0.4em] mt-1 lg:mt-2">
        Inventory_Control_Active
      </p>
    </div>
  </div>

  {/* Optional: Add a subtle status indicator for mobile */}
  <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/5">
    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
    <span className="text-[8px] font-black uppercase tracking-widest text-emerald-500/80">System_Online</span>
  </div>
</div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* MAIN INFO */}
          <div className="bg-zinc-950/50 border border-white/5 p-8 rounded-[2.5rem] space-y-6">
            <input value={newProduct.name} onChange={(e)=>setNewProduct({...newProduct, name: e.target.value})} placeholder="Product Name" className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl p-5 outline-none font-bold uppercase" required />
            <div className="grid grid-cols-2 gap-6">
              <select value={newProduct.category} onChange={(e)=>setNewProduct({...newProduct, category: e.target.value, sizes: [], sizeChart: []})} className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl p-5 outline-none font-bold uppercase">
                {categoriesList.map(cat => <option key={cat} value={cat}>{cat.toUpperCase()}</option>)}
              </select>
              <input value={newProduct.brand} onChange={(e)=>setNewProduct({...newProduct, brand: e.target.value})} placeholder="Brand (e.g. ARCHIVE)" className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl p-5 outline-none font-bold uppercase" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <PriceInput label="MRP" value={newProduct.actualPrice} onChange={(val)=>setNewProduct({...newProduct, actualPrice: val})} />
              <PriceInput label="Sale Price" value={newProduct.salePrice} onChange={(val)=>setNewProduct({...newProduct, salePrice: val})} isEmerald />
            </div>
            <textarea value={newProduct.description} onChange={(e)=>setNewProduct({...newProduct, description: e.target.value})} placeholder="Detailed Description" className="w-full bg-zinc-900/50 border border-white/5 rounded-2xl p-5 outline-none h-32 resize-none text-zinc-400" required />
          </div>
          
          {/* SIZES AND STOCK TABLE */}
          <div className="bg-zinc-950/50 border border-white/5 p-8 rounded-[2.5rem] space-y-8">
            <label className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em]">Step_1: Select Available Sizes</label>
            <div className="flex flex-wrap gap-2">
              {(newProduct.category === "shoes" || newProduct.category === "sneakers" ? shoeSizes : clothingSizes).map((size) => (
                <button key={size} type="button" onClick={() => toggleSize(size)} className={`px-6 py-3 min-w-[70px] rounded-xl text-[10px] font-black border transition-all ${newProduct.sizes.includes(size) ? "bg-emerald-500 border-emerald-500 text-black shadow-[0_0_20px_rgba(16,185,129,0.2)]" : "bg-zinc-900 border-white/5 text-zinc-500 hover:border-white/20"}`}>{size}</button>
              ))}
            </div>

            {newProduct.sizeChart.length > 0 && (
              <div className="space-y-4">
                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em]">Step_2: Configure Measurements & Stock</label>
                <div className="overflow-hidden border border-white/5 rounded-2xl">
                  <table className="w-full text-left text-[10px] font-black uppercase">
                    <thead className="bg-zinc-900 text-zinc-500">
                      <tr>
                        {getChartHeaders().map(h => <th key={h} className="p-4">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 bg-zinc-900/30">
                      {newProduct.sizeChart.map((row, idx) => (
                        <tr key={idx}>
                          <td className="p-4 text-emerald-500 bg-emerald-500/5">{row.label}</td>
                          <td><input value={row.val1} onChange={(e)=>handleSizeChartChange(idx, "val1", e.target.value)} placeholder="Val" className="w-full bg-transparent p-4 outline-none border-l border-white/5" /></td>
                          <td><input value={row.val2} onChange={(e)=>handleSizeChartChange(idx, "val2", e.target.value)} placeholder="Val" className="w-full bg-transparent p-4 outline-none border-l border-white/5" /></td>
                          <td><input value={row.val3} onChange={(e)=>handleSizeChartChange(idx, "val3", e.target.value)} placeholder="Val" className="w-full bg-transparent p-4 outline-none border-l border-white/5" /></td>
                          <td className="bg-emerald-500/20">
                            <input type="number" min="0" value={row.stock} onChange={(e)=>handleSizeChartChange(idx, "stock", e.target.value)} className="w-full bg-transparent p-4 outline-none border-l border-white/10 font-black text-emerald-500 text-center" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {/* MEDIA UPLOAD SECTION */}
          <div className="bg-zinc-950/50 border border-white/5 p-8 rounded-[2.5rem] space-y-6">
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em]">Media_Assets (Limit 4)</p>
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] bg-zinc-900/50 rounded-2xl border border-white/5 relative overflow-hidden group">
                  {getArray(newProduct.images)[i] ? (
                    <div className="relative h-full w-full">
                        <img src={getArray(newProduct.images)[i]} className="w-full h-full object-cover" alt="" crossOrigin="anonymous"/>
                        <button type="button" onClick={() => {
                            const current = getArray(newProduct.images);
                            current.splice(i, 1);
                            setNewProduct({...newProduct, images: current.join(", ")});
                        }} className="absolute top-2 right-2 p-1 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><X size={14}/></button>
                    </div>
                  ) : (
                    <label className={`w-full h-full flex items-center justify-center cursor-pointer hover:bg-zinc-800 transition-colors ${uploading ? 'pointer-events-none' : ''}`}>
                      <input type="file" className="hidden" onChange={async (e) => {
                        const file = e.target.files[0];
                        if(!file) return;
                        const url = await uploadMedia(file);
                        if (url) {
                          const current = getArray(newProduct.images);
                          current.push(url);
                          setNewProduct({ ...newProduct, images: current.join(", ") });
                        }
                      }} />
                      {uploading ? <Loader2 size={20} className="text-emerald-500 animate-spin" /> : <PlusCircle size={20} className="text-zinc-800" />}
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <button disabled={loading || uploading} type="submit" className="w-full bg-white text-black font-black py-6 rounded-[3rem] uppercase italic flex justify-center items-center gap-3 hover:bg-emerald-500 transition-all disabled:opacity-50">
            {loading ? <Loader2 className="animate-spin" /> : <CheckCircle2 size={18} />}
            Finalize_Protocol
          </button>
        </div>
      </form>
    </div>
  );
};

// Reusable Components
const PriceInput = ({ label, value, onChange, isEmerald = false }) => (
  <div className="space-y-2 flex-1">
    <label className="text-[10px] font-black text-zinc-500 italic uppercase">{label}</label>
    <div className="relative">
      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-600 font-bold">₹</span>
      <input type="number" value={value} onChange={(e)=>onChange(e.target.value)} className={`w-full bg-zinc-900/50 border ${isEmerald ? 'border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.1)]' : 'border-white/5'} rounded-2xl p-5 pl-10 text-sm font-black outline-none focus:border-white/20 transition-all`} required />
    </div>
  </div>
);

const CheckCircle2 = ({size}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="m9 12 2 2 4-4"/></svg>;

export default AddProduct;